<script setup lang="ts">
import { Head, Link } from '@inertiajs/vue3'
import { } from "@fal"
import { faCheckCircle, faTimesCircle } from "@fas"

import PageHeading from "@/Components/Headings/PageHeading.vue"
import { PageHeading as PageHeadingTS } from '@/types/PageHeading'

import { FontAwesomeIcon } from '@fortawesome/vue-fontawesome'
import { faExclamationTriangle } from '@fad'
import { library } from "@fortawesome/fontawesome-svg-core"
import { onMounted, ref } from 'vue'
import Chart from 'primevue/chart'
library.add(faCheckCircle, faTimesCircle, faExclamationTriangle)

const props = defineProps<{
    pageHead?: PageHeadingTS
    title: string
    stats: {
        name: string
        change: string
        changeType: 'positive' | 'negative'
        value: string | number
        route?: {
            name: string
            parameters?: Record<string, any>
        }
    }[]
    low_stock_product: {}[]
    graph_product_by_order_quantity: {

    }
    graph_product_by_type: {

    }
}>()
const pallet_limits = 'limit'

// Section: chart pie
const setChartOptions = () => {
    const documentStyle = getComputedStyle(document.documentElement)
    const textColor = documentStyle.getPropertyValue('--p-text-color')

    return {
        plugins: {
            legend: {
                labels: {
                    usePointStyle: true,
                    color: textColor
                }
            }
        }
    };
};
const chartOptions = ref()
const isChartLineLoaded = ref(false)
onMounted(() => {
    chartOptions.value = setChartOptions()
})

// Section: chart line
const setOptionsChartLine = () => {
    const documentStyle = getComputedStyle(document.documentElement);
    const textColor = documentStyle.getPropertyValue('--p-text-color');
    const textColorSecondary = documentStyle.getPropertyValue('--p-text-muted-color');
    const surfaceBorder = documentStyle.getPropertyValue('--p-content-border-color');

    return {
        maintainAspectRatio: false,
        aspectRatio: 0.6,
        plugins: {
            legend: {
                labels: {
                    color: textColor
                }
            }
        },
        scales: {
            x: {
                ticks: {
                    color: textColorSecondary
                },
                grid: {
                    color: surfaceBorder
                }
            },
            y: {
                ticks: {
                    color: textColorSecondary
                },
                grid: {
                    color: surfaceBorder
                }
            }
        }
    };
}
const isChartLoaded = ref(false)
const optionsChartLine = ref<typeof setOptionsChartLine | null>(null)
onMounted(() => {
    optionsChartLine.value = setOptionsChartLine()
})
</script>

<template>

    <Head :title="title" />
    <PageHeading :data="pageHead"></PageHeading>

    <div v-if="pallet_limits && low_stock_product.length" class="p-4">
        <div class="p-4 rounded-md"
            :class="{
                'bg-yellow-50': pallet_limits === 'almost',
                'bg-orange-200': pallet_limits === 'limit',
                'bg-red-200': pallet_limits === 'exceeded',
            }"
        >
            <div class="flex" :class="{
                'text-amber-500': pallet_limits === 'almost',
                'text-orange-600': pallet_limits === 'limit',
                'text-red-600': pallet_limits === 'exceeded',
            }">
                <div class="flex items-center">
                    <font-awesome-icon :icon="['fad', 'exclamation-triangle']" class="text-2xl"
                        aria-hidden="true"
                        fixed-width
                        
                    />
                </div>
                <div class="ml-3">
                    <h3 class="text-sm font-medium">Produk berikut memiliki stok rendah:</h3>
                    <div class="text-xs opacity-90 ">
                        <p v-for="product in low_stock_product">
                            {{ product.name }} ({{ product.quantity }})
                        </p>
                    </div>
                </div>
            </div>
        </div>
    </div>
    
    <!-- <dl class="mx-auto grid grid-cols-1 gap-px bg-gray-900/5 sm:grid-cols-2 lg:grid-cols-4 w-full">
        <div class="flex flex-wrap items-baseline justify-between gap-x-4 gap-y-2 bg-white px-4 py-10 sm:px-6 xl:px-8">
            <dt class="text-sm/6 font-medium text-gray-500">Produk</dt>
            <dd class="w-full flex-none text-3xl/10 font-medium tracking-tight">
                <Link :href="route('master.products.index')" class="primaryLink">
                    {{ stats.product.count }}
                </Link>
            </dd>
        </div>
    </dl> -->

    <div class="py-2">
        <div class="py-6 mx-auto max-w-2xl px-6 lg:max-w-7xl lg:px-8">
            <!-- <h2 class="text-base/7 font-semibold text-indigo-400">Deploy faster</h2>
            <p class="mt-2 max-w-lg text-pretty text-4xl font-semibold tracking-tight sm:text-5xl">Everything you need to deploy your app</p> -->
            <div class="grid grid-cols-1 gap-6 lg:grid-cols-6">
                <!-- Chart: line (kuantitas) -->
                <div class="flex p-px lg:col-span-4 h-full">
                    <div class="py-4 px-3 w-full overflow-hidden rounded ring-1 ring-black/15 max-lg:rounded-t-[0.75rem] lg:rounded-tl-[0.75rem]">
                        <!-- <img class="h-80 object-cover object-left" src="https://tailwindui.com/plus-assets/img/component-images/bento-02-releases.png" alt="" /> -->
                        <Chart v-show="isChartLoaded" @loaded="isChartLoaded = true" type="line" :data="graph_product_by_order_quantity" :options="optionsChartLine" class="h-72" />
                        <div v-show="!isChartLoaded" class="h-72 skeleton w-full"></div>

                        <div class="p-7">
                            <!-- <h3 class="text-sm/4 font-semibold text-gray-400">Releases</h3> -->
                            <p class="mt-2 text-lg font-medium tracking-tight">Jumlah kuantitas produk dari order</p>
                            <p class="mt-2 max-w-lg text-sm/6 text-gray-400">
                                Menunjukkan kalkulasi jumlah kuantitas orderan berdasarkan tipe produk, apakah polos, custom, atau tutup. Kuantitas berdasarkan dari order, bukan kuantitas validasi ataupun kuantitas invoice.
                            </p>
                        </div>
                    </div>
                </div>

                <!-- Chart: pie (type) -->
                <div class="flex p-px lg:col-span-2 h-full">
                    <div class="py-4 px-3 overflow-hidden pt-5 rounded bg-gray-x100 ring-1 ring-gray-300 w-full lg:rounded-tr-[0.75rem]">
                        <Chart v-show="isChartLineLoaded" type="pie" @loaded="isChartLineLoaded = true" :data="graph_product_by_type" :options="chartOptions" class="h-auto w-72 max-w-full mx-auto" />
                        <div v-show="!isChartLineLoaded" class="skeleton h-72 w-72 max-w-full mx-auto"></div>
                        <!-- <img class="h-80 object-cover" src="https://tailwindui.com/plus-assets/img/component-images/bento-02-integrations.png" alt="" /> -->
                        <div class="p-7">
                            <!-- <h3 class="text-sm/4 font-semibold text-gray-400">Integrations</h3> -->
                            <p class="mt-2 text-lg font-medium tracking-tight">Jumlah produk berdasarkan tipe</p>
                            <p class="mt-2 max-w-lg text-sm/6 text-gray-400">Kalkulasi produk berdasarkan tipe polos, tutup, custom.</p>
                        </div>
                    </div>
                </div>
            </div>

            <!-- <div class="mt-4 grid grid-cols-1 gap-4 sm:mt-8 lg:grid-cols-6">
                <div class="flex p-px lg:col-span-2">
                    <div class="overflow-hidden rounded bg-gray-100 ring-1 ring-black/15 lg:rounded-bl-[0.75rem]">
                        <img class="h-80 object-cover" src="https://tailwindui.com/plus-assets/img/component-images/bento-02-security.png" alt="" />
                        <div class="p-10">
                            <h3 class="text-sm/4 font-semibold text-gray-400">Security</h3>
                            <p class="mt-2 text-lg font-medium tracking-tight">Advanced access control</p>
                            <p class="mt-2 max-w-lg text-sm/6 text-gray-400">Vestibulum ante ipsum primis in faucibus orci luctus et ultrices posuere cubilia.</p>
                        </div>
                    </div>
                </div>

                <div class="flex p-px lg:col-span-4">
                    <div class="overflow-hidden rounded bg-gray-100 ring-1 ring-black/15 max-lg:rounded-b-[0.75rem] lg:rounded-br-[0.75rem]">
                        <img class="h-80 object-cover object-left" src="https://tailwindui.com/plus-assets/img/component-images/bento-02-performance.png" alt="" />
                        <div class="p-10">
                            <h3 class="text-sm/4 font-semibold text-gray-400">Performance</h3>
                            <p class="mt-2 text-lg font-medium tracking-tight">Lightning-fast builds</p>
                            <p class="mt-2 max-w-lg text-sm/6 text-gray-400">Sed congue eros non finibus molestie. Vestibulum euismod augue vel commodo vulputate. Maecenas at augue sed elit dictum vulputate.</p>
                        </div>
                    </div>
                </div>
            </div> -->
        </div>
    </div>
</template>